import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Flex } from "theme-ui";
import H5 from "components/typography/H5";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import { getCurrentMulchMap, getMulchMap } from "api/MulchMap";
import { MulchMap } from "api/MulchMap/types";
import { themevals } from "theme/themevals";
import moment from "moment-timezone";
import AttachMapModal from "components/AttachMapModal";

const SQFT_PER_SQ_METER = 10.7639104167;
const SQFT_PER_CU_YD_PER_INCH = 324;

function computeCubicYards(map: MulchMap): { cuYd: number; sqFt: number } {
  if (!map?.zones?.length) return { cuYd: 0, sqFt: 0 };
  let cuYd = 0;
  let sqFt = 0;
  for (const z of map.zones) {
    const sqFtZ = z.areaSqMeters * SQFT_PER_SQ_METER;
    cuYd += (sqFtZ * z.depthInches) / SQFT_PER_CU_YD_PER_INCH;
    sqFt += sqFtZ;
  }
  return { cuYd: Math.round(cuYd * 10) / 10, sqFt: Math.round(sqFt) };
}

function buildStaticMapUrl(
  map: MulchMap,
  size = "480x260",
): string | null {
  const provider = map.basemap || "google_hybrid";
  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;
  if (provider === "mapbox_sat" && mapboxToken) {
    return buildMapboxStaticUrl(map, size, mapboxToken);
  }
  return buildGoogleStaticUrl(map, size);
}

function buildGoogleStaticUrl(map: MulchMap, size: string): string | null {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (!apiKey) return null;
  const center =
    map.mapCenterLat && map.mapCenterLng
      ? `${map.mapCenterLat},${map.mapCenterLng}`
      : map.location?.latitude && map.location?.longitude
        ? `${map.location.latitude},${map.location.longitude}`
        : null;
  const zoom = Math.min(Math.max(map.mapZoom || 19, 15), 20);

  let url = `https://maps.googleapis.com/maps/api/staticmap?size=${size}&scale=2&maptype=satellite&key=${apiKey}`;
  if (center) url += `&center=${center}&zoom=${zoom}`;

  for (const z of map.zones || []) {
    if (!z.path?.length || z.path.length < 3) continue;
    const hex = (z.color || "#667E4E").replace("#", "");
    const path = z.path
      .map((p) => `${p.lat.toFixed(6)},${p.lng.toFixed(6)}`)
      .join("|");
    const closing = `${z.path[0].lat.toFixed(6)},${z.path[0].lng.toFixed(6)}`;
    url += `&path=color:0x${hex}FF|fillcolor:0x${hex}73|weight:3|${path}|${closing}`;
  }
  return url;
}

function buildMapboxStaticUrl(
  map: MulchMap,
  size: string,
  token: string,
): string | null {
  const [w, h] = size.split("x").map((n) => parseInt(n, 10));
  if (!w || !h) return null;
  const lat =
    map.mapCenterLat ?? map.location?.latitude ?? null;
  const lng =
    map.mapCenterLng ?? map.location?.longitude ?? null;
  if (lat == null || lng == null) return null;
  const zoom = Math.min(Math.max(map.mapZoom || 19, 15), 20);

  // Mapbox static overlays use hex without '#', comma-separated RGBA hex.
  // Overlay is a GeoJSON path with an encoded fill.
  const overlays = (map.zones || [])
    .filter((z) => z.path?.length && z.path.length >= 3)
    .map((z) => {
      const stroke = (z.color || "#667E4E").replace("#", "");
      const closedPath = [...z.path, z.path[0]];
      const geojson = {
        type: "Feature",
        properties: {
          stroke: `#${stroke}`,
          "stroke-width": 2,
          "stroke-opacity": 1,
          fill: `#${stroke}`,
          "fill-opacity": 0.45,
        },
        geometry: {
          type: "Polygon",
          coordinates: [closedPath.map((p) => [p.lng, p.lat])],
        },
      };
      return `geojson(${encodeURIComponent(JSON.stringify(geojson))})`;
    })
    .join(",");

  const overlaySegment = overlays ? `/${overlays}` : "";
  return `https://api.mapbox.com/styles/v1/mapbox/satellite-v9/static${overlaySegment}/${lng},${lat},${zoom},0/${w}x${h}@2x?access_token=${token}`;
}

interface Props {
  locationId: string | null | undefined;
  /**
   * Session 20 Commit 4 \u2014 optional specific Map version (mulchMap.id) that
   * this record is attached to. When present, this section renders THAT
   * exact version regardless of what the Location's current Map is now.
   * When null/undefined AND a `recordType`/`recordId` are provided, this
   * section falls back to showing an "Attach map" button.
   *
   * Falling back to "current Map at Location" (the pre-Session-20 behavior)
   * only happens when NO record context is passed at all \u2014 that's used by
   * views that don't own an attach state (e.g. Location detail preview).
   */
  mulchMapId?: string | null;
  /**
   * When both `recordType` and `recordId` are provided, the section shows
   * the Attach action for unattached records and passes them through to
   * the AttachMapModal. Without them, no Attach button is offered.
   */
  recordType?: "quote_request" | "quote" | "project" | "invoice" | "purchase_order";
  recordId?: string;
  /**
   * Called after a successful attach/detach with the new mulchMapId value.
   * Parent should refetch the record so the FK stays in sync.
   */
  onAttachChanged?: (newMulchMapId: string | null) => void;

  showEditButton?: boolean;
  showSendToProvider?: boolean;
  onSendToProvider?: () => void;
  /**
   * Session 27 - when true, this section renders a specific attached map
   * read-only: no Attach/Change/Draw affordances, and no fallback to the
   * Location's current map when mulchMapId is null/undefined. Used by
   * views that inherit their map from a parent record (e.g. Purchase Order
   * detail, which inherits from its Project).
   */
  readonly?: boolean;
}

const SiteMapSection: React.FC<Props> = ({
  locationId,
  mulchMapId,
  recordType,
  recordId,
  onAttachChanged,
  showEditButton = true,
  showSendToProvider = false,
  onSendToProvider,
  readonly = false,
}) => {
  const navigate = useNavigate();
  const [map, setMap] = useState<MulchMap | null>(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [attachModalOpen, setAttachModalOpen] = useState(false);

  // Session 25 fix: local override for mulchMapId that trumps the
  // prop after a successful attach. The parent's refetch may hit a
  // stale Galera read node and return the old (null) mulchMapId,
  // which would revert the UI to "unattached" even though the DB was
  // updated. Local override stays until either (a) the prop catches
  // up and matches, or (b) the component unmounts.
  //
  // `undefined` = no override active; use the prop as-is.
  // `null` or `string` = override; use this value.
  const [localMulchMapId, setLocalMulchMapId] = useState<
    string | null | undefined
  >(undefined);
  const effectiveMulchMapId =
    localMulchMapId !== undefined ? localMulchMapId : mulchMapId;

  // Whether the parent record supports attach/detach. When true, an
  // unattached state shows the Attach action instead of the legacy
  // "current Map at Location" fallback.
  const attachable = !!(recordType && recordId);

  useEffect(() => {
    if (!locationId) {
      setMap(null);
      setChecked(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setChecked(false);

    (async () => {
      let res: any = null;
      if (effectiveMulchMapId) {
        // Attached to a specific version — render that one exactly.
        res = await getMulchMap(effectiveMulchMapId);
      } else if (!attachable && !readonly) {
        // Legacy fallback — no record context, just show the location's
        // current Map (whatever the alias resolves to). Skipped in readonly
        // mode: the caller wants "only what's explicitly attached", not
        // "guess from address".
        res = await getCurrentMulchMap(locationId);
      }
      // If attachable and unattached, we intentionally leave `map` null
      // so the Attach action is shown instead of a preview.
      if (cancelled) return;
      setMap(res && (res as any).id ? res : null);
      setLoading(false);
      setChecked(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [locationId, effectiveMulchMapId, attachable, readonly]);

  if (!locationId) return null;

  const { cuYd, sqFt } = map ? computeCubicYards(map) : { cuYd: 0, sqFt: 0 };
  const staticUrl = map ? buildStaticMapUrl(map) : null;
  const showAttachAction = attachable && !readonly && !effectiveMulchMapId && !loading && checked;
  const showAttachedControls = attachable && !readonly && !!effectiveMulchMapId && !!map;

  return (
    <Box sx={{ paddingRight: "30px", paddingTop: "10px" }}>
      <H5
        fontFamily={"Poppins"}
        color={themevals.colors.black}
        fontSize={"14px"}
        fontWeight={600}
        marginTop={"30px"}
        marginBottom={"12px"}
      >
        Site map
      </H5>

      {loading && (
        <P fontSize={"12px"} color={themevals.colors.dkgrey}>
          Loading…
        </P>
      )}

      {/* Attachable + not yet attached */}
      {showAttachAction && (
        <>
          <P
            fontFamily={"Poppins"}
            color={themevals.colors.dkgrey}
            fontSize={"12px"}
            marginTop={"0px"}
            marginBottom={"12px"}
          >
            No map attached to this record yet.
          </P>
          <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
            <BasicButton
              type="button"
              scheme="primary"
              onClick={() => setAttachModalOpen(true)}
              styles={{ margin: "0" }}
              keepText
            >
              Attach map
            </BasicButton>
            {showEditButton && !readonly && (
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={() => navigate(`/mulch-calculator/${locationId}/edit`)}
                styles={{ margin: "0" }}
                keepText
              >
                Draw new map
              </BasicButton>
            )}
          </Flex>
        </>
      )}

      {/* Readonly + no map — parent record inherits from elsewhere */}
      {!loading && checked && !map && readonly && (
        <P
          fontFamily={"Poppins"}
          color={themevals.colors.dkgrey}
          fontSize={"12px"}
          marginTop={"0px"}
          marginBottom={"0px"}
        >
          No map attached to this record.
        </P>
      )}

      {/* Legacy fallback (no record context) and no map found */}
      {!loading && checked && !map && !attachable && !readonly && (
        <>
          <P
            fontFamily={"Poppins"}
            color={themevals.colors.dkgrey}
            fontSize={"12px"}
            marginTop={"0px"}
            marginBottom={"12px"}
          >
            No map drawn for this site yet.
          </P>
          {showEditButton && (
            <BasicButton
              type="button"
              scheme="secondary"
              outline
              onClick={() => navigate(`/mulch-calculator/${locationId}/edit`)}
              styles={{ margin: "0" }}
              keepText
            >
              Draw map
            </BasicButton>
          )}
        </>
      )}

      {!loading && map && (
        <>
          {staticUrl && !readonly && (
            <Box
              onClick={() => {
                // When we know which specific Map this is, deep-link to
                // that Map's canonical URL. Otherwise fall back to the
                // Location alias URL (which resolves to whatever's current).
                if (map.location?.id && map.mapGroupId) {
                  navigate(
                    `/mulch-calculator/${map.location.id}/maps/${map.mapGroupId}/edit`,
                  );
                } else {
                  navigate(`/mulch-calculator/${locationId}/edit`);
                }
              }}
              sx={{
                cursor: "pointer",
                borderRadius: "6px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "inputOutline",
                marginBottom: "10px",
                lineHeight: 0,
                width: "100%",
              }}
            >
              <img
                src={staticUrl}
                alt="Site map"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </Box>
          )}
          {map.name && (
            <P
              fontFamily={"Poppins"}
              fontSize={"12px"}
              fontWeight={600}
              color={themevals.colors.black}
              marginTop={"0px"}
              marginBottom={"4px"}
            >
              {map.name}
              {map.version ? ` \u00b7 v${map.version}` : ""}
            </P>
          )}
          <Flex
            sx={{
              justifyContent: "space-between",
              alignItems: "baseline",
              marginBottom: "8px",
            }}
          >
            <P
              fontFamily={"Poppins"}
              fontSize={"14px"}
              fontWeight={600}
              color={themevals.colors.black}
              marginTop={"0px"}
              marginBottom={"0px"}
            >
              {cuYd.toFixed(1)} cu yd
            </P>
            <P
              fontFamily={"Poppins"}
              fontSize={"11px"}
              color={themevals.colors.dkgrey}
              marginTop={"0px"}
              marginBottom={"0px"}
            >
              {sqFt.toLocaleString()} sq ft
            </P>
          </Flex>
          <P
            fontFamily={"Poppins"}
            fontSize={"11px"}
            color={themevals.colors.dkgrey}
            marginTop={"0px"}
            marginBottom={"12px"}
          >
            {map.zones?.length || 0} zone
            {(map.zones?.length || 0) === 1 ? "" : "s"} · last edited{" "}
            {moment(map.updatedAt).fromNow()}
          </P>
          <Flex sx={{ gap: "8px", flexWrap: "wrap" }}>
            {/*
             * Session 30 fix: gate "Open map" on !readonly. The mulch-
             * calculator route is admin-only and 403s for clients /
             * providers. Every other control in this component already
             * checks readonly — this one was missed.
             */}
            {showEditButton && !readonly && (
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={() => {
                  if (map.location?.id && map.mapGroupId) {
                    navigate(
                      `/mulch-calculator/${map.location.id}/maps/${map.mapGroupId}/edit`,
                    );
                  } else {
                    navigate(`/mulch-calculator/${locationId}/edit`);
                  }
                }}
                styles={{ margin: "0" }}
                keepText
              >
                Open map
              </BasicButton>
            )}
            {showAttachedControls && (
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={() => setAttachModalOpen(true)}
                styles={{ margin: "0" }}
                keepText
              >
                Change attached map
              </BasicButton>
            )}
            {showSendToProvider && onSendToProvider && (
              <BasicButton
                type="button"
                scheme="secondary"
                outline
                onClick={onSendToProvider}
                styles={{ margin: "0" }}
                keepText
              >
                Send to provider
              </BasicButton>
            )}
          </Flex>
        </>
      )}

      {attachModalOpen && attachable && recordType && recordId && (
        <AttachMapModal
          locationId={locationId}
          recordType={recordType}
          recordId={recordId}
          currentMulchMapId={effectiveMulchMapId ?? null}
          onClose={() => setAttachModalOpen(false)}
          onAttached={(next) => {
            // Session 25 fix: capture locally so the section refreshes
            // to the new value immediately, without depending on the
            // parent's refetch surviving a Galera stale read.
            setLocalMulchMapId(next);
            if (onAttachChanged) onAttachChanged(next);
          }}
        />
      )}
    </Box>
  );
};

export default SiteMapSection;
